/**
 * «Фінанси» — управлінський облік без налаштування (ч.1364).
 *
 * Зведення за період: прибуток (виручка → собівартість → витрати за категоріями), гроші
 * (надходження / витрати за статтями, залишки), борги, план виручки на місяць; документи за
 * рядком зведення; план виручки по місяцях року.
 */
import { api } from './client';

export interface FinLine {
  /** Ключі рядка для «провалитись» (через кому: acct:702, item:<id>, cat:salary, cfi:<id>, op:TO_SUPPLIER). */
  key: string;
  name: string;
  amount: number;
  prev: number;
}

export interface FinLines { total: number; prev: number; lines: FinLine[] }

export interface FinPeriod { start: string; end: string; label: string; months: number | null }

export interface FinProfit {
  revenue: FinLines;
  cogs: FinLines;
  other_income: FinLines;
  expenses: FinLines;
  gross_profit: number;
  gross_profit_prev: number;
  gross_margin_pct: number | null;
  profit: number;
  profit_prev: number;
  profit_margin_pct: number | null;
}

export interface FinPlan {
  available: boolean;
  amount: number | null;
  progress_pct: number | null;
  months: number;
  planned_months: number;
  editable_month: { year: number; month: number } | null;
  reason: string | null;
}

export interface FinBalance { total: number; bank: number; cash: number }

export interface FinCash {
  opening: FinBalance;
  closing: FinBalance;
  inflow: FinLines;
  outflow: FinLines;
  net: number;
  net_prev: number;
  internal_transfers: number;
}

export interface FinDebtSide {
  total: number;
  count: number;
  overdue: number;
  overdue_docs: number;
  top: { contractor_id: string; name: string; amount: number }[];
}

export interface FinDebts { as_of: string; receivable: FinDebtSide; payable: FinDebtSide }

export interface FinSummary {
  period: FinPeriod;
  previous: FinPeriod;
  organization_ids: string[];
  organizations: { id: string; code: string; name: string }[];
  currencies: string[];
  warnings: string[];
  profit: FinProfit | null;
  plan: FinPlan | null;
  cash: FinCash | null;
  debts: FinDebts | null;
}

export type FinMetric = 'revenue' | 'other_income' | 'cogs' | 'expense' | 'cash_in' | 'cash_out';

export interface FinDrillItem {
  document_type: string;
  document_id: string;
  number: string | null;
  date: string | null;
  organization_id: string;
  party: string | null;
  amount: number;
}

export interface FinDrill { items: FinDrillItem[]; count: number; total: number; truncated: boolean }

export interface RevenuePlanMonth { month: number; amount: number | null; note: string | null }
export interface RevenuePlanYear { organization_id: string; year: number; months: RevenuePlanMonth[] }

export interface FinScope {
  period_start: string;
  period_end: string;
  /** Одна організація; без неї — усі, де є право на управлінські звіти. */
  organization_id?: string;
}

const SILENT = { headers: { 'X-Silent-Toast': '1' } };

export const finance = {
  summary: (p: FinScope) => api.get<FinSummary>('/finance/summary', { params: p }).then((r) => r.data),
  drill: (p: FinScope & { metric: FinMetric; keys: string }) =>
    api.get<FinDrill>('/finance/drill', { params: p }).then((r) => r.data),
  revenuePlan: (organizationId: string, year: number) =>
    api.get<RevenuePlanYear>('/finance/revenue-plan', { params: { organization_id: organizationId, year } })
      .then((r) => r.data),
  setRevenuePlan: (p: { organization_id: string; year: number; month: number; amount: number | null;
    note?: string | null; apply_to?: 'month' | 'rest_of_year' }) =>
    api.put<RevenuePlanYear>('/finance/revenue-plan', p, SILENT).then((r) => r.data),
};

/** Межі календарного місяця у форматі YYYY-MM-DD. */
export function monthBounds(year: number, month: number): { period_start: string; period_end: string } {
  const pad = (n: number) => String(n).padStart(2, '0');
  const last = new Date(year, month, 0).getDate();
  return { period_start: `${year}-${pad(month)}-01`, period_end: `${year}-${pad(month)}-${pad(last)}` };
}
